import { useLocation } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Modal } from '../ui'
import { useKBArticles } from '../../hooks/useKBArticles'
import type { KBArticle } from '../../data/kb-articles/types'
import { Clock, ArrowRight, ChevronRight, ChevronLeft, BookMarked } from 'lucide-react'

function estimateReadTime(content?: string) {
  if (!content) return null

  const plainText = content
    .replace(/[#*_>`~[\]()]/g, ' ')
    .trim()

  const words = plainText.match(/\b[\w']+\b/g)?.length ?? 0

  if (!words) return null

  const minutes = Math.max(1, Math.ceil(words / 180))

  return `${minutes} min read`
}

function getFrameKeyFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/frame-(\d+)$/)
  if (!match) return null
  return `frame-${match[1]}`
}

export default function KBArticleSection() {
  const location = useLocation()
  const frameKey = getFrameKeyFromPath(location.pathname)

  const { articles, loading, error } = useKBArticles(frameKey)

  const [selectedArticle, setSelectedArticle] = useState<KBArticle | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [contentHeight, setContentHeight] = useState(0)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const contentRef = useRef<HTMLDivElement | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const hasHorizontalOverflow = articles.length > 3
  const visibleCardCount = Math.min(articles.length, 3)
  const bodyPaddingClass =
    visibleCardCount < 3
      ? 'px-4 pb-5 pt-5'
      : 'px-5 pb-5 pt-5'
  const CARD_STEP = 244

  const sectionWidthClass =
    visibleCardCount === 1
      ? 'max-w-[275px]'
      : visibleCardCount === 2
        ? 'max-w-[500px]'
        : 'max-w-[765px]'

  const railWidthClass =
    visibleCardCount === 1
      ? 'w-full flex justify-center'
      : visibleCardCount === 2
        ? 'w-[464px]'
        : 'w-[708px]'

  const updateScrollState = () => {
    const el = scrollRef.current
    if (!el || !hasHorizontalOverflow) {
      setCanScrollLeft(false)
      setCanScrollRight(false)
      return
    }

    const maxScrollLeft = el.scrollWidth - el.clientWidth
    setCanScrollLeft(el.scrollLeft > 2)
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 2)
  }

  const scrollByStep = (direction: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return

    el.scrollBy({
      left: direction === 'right' ? CARD_STEP : -CARD_STEP,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight)
    }
  }, [articles, isExpanded, railWidthClass])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    updateScrollState()

    const handleScroll = () => updateScrollState()
    el.addEventListener('scroll', handleScroll, { passive: true })

    const raf = requestAnimationFrame(() => {
      updateScrollState()
    })

    return () => {
      el.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(raf)
    }
  }, [articles.length, isExpanded, hasHorizontalOverflow])

  if (!frameKey) return null
  if (loading || error || !articles?.length) return null

  return (
    <>
      <section className={`mt-11 ${sectionWidthClass}`}>
        <div className="mb-8 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-black/5 transition-all duration-200 hover:shadow-md hover:ring-[#65ADC1]/50">
          <button
            type="button"
            onClick={() => setIsExpanded(prev => !prev)}
            className={`flex w-full items-center justify-between px-5 py-4 text-left transition-colors duration-300 focus:outline-none ${
              isExpanded ? 'bg-[#E7F2F6]' : 'bg-white hover:bg-[#E7F2F6]/60'
            }`}
            aria-expanded={isExpanded}
            aria-controls="kb-articles-content"
          >
            <span className="text-xl font-semibold text-cyan-900">
              Helpful Resources ({articles.length})
            </span>

            <ChevronRight
              size={22}
              className={`text-cyan-900 transition-transform duration-300 ease-in-out ${
                isExpanded ? 'rotate-90' : 'rotate-0'
              }`}
            />
          </button>

          <div
            id="kb-articles-content"
            className="overflow-hidden border-t border-stone-200 bg-[#E7F2F6]/30 transition-[max-height] duration-500 ease-in-out"
            style={{ maxHeight: isExpanded ? `${contentHeight + 48}px` : '0px' }}
          >
            <div className={bodyPaddingClass}>
              <div
                ref={contentRef}
                className={visibleCardCount === 1 ? 'flex justify-center' : railWidthClass}
              >
                <div className="relative">
                  {hasHorizontalOverflow && canScrollLeft && (
                    <button
                      type="button"
                      onClick={() => scrollByStep('left')}
                      aria-label="Scroll articles left"
                      className="absolute left-2 top-[calc(50%-10px)] z-10 -translate-y-1/2 rounded-full border border-stone-200 bg-white/95 p-2 text-cyan-900 shadow-sm transition-all duration-200 hover:bg-white hover:shadow-md focus:outline-none"
                    >
                      <ChevronLeft size={18} />
                    </button>
                  )}

                  {hasHorizontalOverflow && canScrollRight && (
                    <button
                      type="button"
                      onClick={() => scrollByStep('right')}
                      aria-label="Scroll articles right"
                      className="absolute right-2 top-[calc(50%-10px)] z-10 -translate-y-1/2 rounded-full border border-stone-200 bg-white/95 p-2 text-cyan-900 shadow-sm transition-all duration-200 hover:bg-white hover:shadow-md focus:outline-none"
                    >
                      <ChevronRight size={18} />
                    </button>
                  )}

                  <div
                    ref={scrollRef}
                    className={`
                      pt-2
                      snap-x snap-mandatory
                      ${hasHorizontalOverflow ? 'overflow-x-auto pb-5' : 'overflow-x-hidden pb-0'}
                      scrollbar-thin scrollbar-track-transparent scrollbar-thumb-[#C7CDD1]
                      hover:scrollbar-thumb-[#B8C0C5]
                    `}
                  >
                    <div className={`flex gap-6 ${visibleCardCount < 3 ? 'justify-center' : 'w-max'}`}>
                      {articles.map(article => {
                        const readTime = estimateReadTime(article.content?.external)

                        return (
                          <button
                            key={article.id}
                            onClick={() => setSelectedArticle(article)}
                            className="group w-[215px] flex-shrink-0 snap-start cursor-pointer overflow-hidden rounded-xl border border-stone-300 bg-white text-left transition-all duration-200 hover:-translate-y-1 hover:shadow-lg outline-none focus:outline-none active:outline-none"
                          >
                            <div className="relative flex h-[96px] items-center justify-center bg-[#DDDDDD] transition-colors duration-200 group-hover:bg-[#CFCFCF]">
                              {readTime && (
                                <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-[11px] font-medium text-[#1F4F5F] opacity-0 transition-all duration-200 group-hover:opacity-100">
                                  <Clock size={12} strokeWidth={2} />
                                  <span>{readTime}</span>
                                </div>
                              )}

                              <BookMarked
                                size={28}
                                className="text-white transition-all duration-200 group-hover:scale-105 group-hover:text-[#1F4F5F]"
                              />
                            </div>

                            <div className="flex h-[82px] items-start p-4">
                              <div className="flex items-start gap-2 text-sm font-medium leading-snug text-stone-800">
                                <span className="flex-1">{article.title}</span>

                                <ArrowRight
                                  size={18}
                                  className="mt-[2px] flex-shrink-0 text-stone-500 transition-all duration-200 group-hover:translate-x-[2px] group-hover:text-[#1F4F5F]"
                                />
                              </div>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Modal
        open={!!selectedArticle}
        onClose={() => setSelectedArticle(null)}
        title={selectedArticle?.title || ''}
        cancelText="Close"
        containerClassName="max-w-2xl rounded-2xl"
        headerClassName="border-b border-cyan-950/20 bg-[#1F4F5F]"
        titleClassName="text-xl font-semibold tracking-tight text-white"
        closeButtonClassName="-mr-2 text-white/80 hover:text-white"
        contentClassName="bg-white"
        footerClassName="bg-gray-50"
      >
        {selectedArticle && (
          <div className="max-h-[70vh] overflow-y-auto pr-2">
            <div
              className="text-stone-700 leading-relaxed
                [&_h1]:mt-2 [&_h1]:mb-4 [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:text-stone-900
                [&_h2]:mt-6 [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-[#1F4F5F]
                [&_h3]:mt-5 [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-stone-900
                [&_p]:my-3 [&_p]:text-[15px] [&_p]:leading-7
                [&_ul]:my-3 [&_ul]:ml-6 [&_ul]:list-disc
                [&_ol]:my-3 [&_ol]:ml-6 [&_ol]:list-decimal
                [&_li]:my-1
                [&_strong]:font-semibold [&_strong]:text-stone-900
                [&_em]:italic
                [&_a]:text-cyan-700 [&_a]:underline
                [&_blockquote]:my-4 [&_blockquote]:border-l-4 [&_blockquote]:border-cyan-200 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-stone-600
                [&_hr]:my-6 [&_hr]:border-stone-200"
            >
              <ReactMarkdown
                allowedElements={[
                  'h1',
                  'h2',
                  'h3',
                  'p',
                  'ul',
                  'ol',
                  'li',
                  'strong',
                  'em',
                  'a',
                  'blockquote',
                  'hr',
                ]}
                components={{
                  img: () => null,
                  a: ({ children, ...props }) => (
                    <a {...props} target="_blank" rel="noreferrer noopener">
                      {children}
                    </a>
                  ),
                }}
              >
                {selectedArticle.content.external}
              </ReactMarkdown>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}